import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = [".open"]
const _hoisted_2 = {
  id: "modal-container-element",
  class: "modal-content"
}
const _hoisted_3 = { columns: "2" }
const _hoisted_4 = ["value"]
const _hoisted_5 = ["value"]
const _hoisted_6 = {
  key: 0,
  columns: "1"
}
const _hoisted_7 = ["value"]
const _hoisted_8 = { columns: "2" }
const _hoisted_9 = ["value"]
const _hoisted_10 = ["value"]
const _hoisted_11 = ["value"]
const _hoisted_12 = { columns: "1" }
const _hoisted_13 = ["checked"]
const _hoisted_14 = {
  key: 1,
  columns: "1"
}
const _hoisted_15 = ["checked"]
const _hoisted_16 = { slot: "footer" }
const _hoisted_17 = ["disabled"]

import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import { EPINIO_TYPES } from '../../types';
import { epinioExceptionToErrorsArray } from '../../utils/errors';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import Banner from '@components/Banner/Banner.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'GitConfigModal',
  setup(__props, { expose: __expose }) {

const store = useStore() as any;
const t = store.getters['i18n/t'];

// Only admins may create global git configurations (the backend rejects a
// non-admin global create with 403). Hide the checkbox for everyone else so the
// option is never offered when it can't be used.
const isAdmin = computed(() => store.getters['epinio/isAdmin']?.() ?? false);

// Modal open state
const showModal = ref(false);
const modalMode = ref<'create' | 'edit' | 'view'>('create');

// Form fields (separate from the model to avoid proxy mutation issues)
const gitConfigId = ref('');
const gitConfigUrl = ref('');
const gitConfigProvider = ref('');
const gitConfigUsername = ref('');
const gitConfigPassword = ref('');
const gitConfigCerts = ref('');
const gitConfigSkipSSL = ref(false);
const gitConfigGlobal = ref(false);

const saving = ref(false);
const errors = ref<string[]>([]);
const hasAssociatedApps = ref<boolean>(false);

const providerOptions = [{
  label: 'Git',
  value: 'git'
}, {
  label: 'GitHub',
  value: 'github'
}, {
  label: 'GitHub Enterprise Cloud',
  value: 'github_enterprise_cloud'
}, {
  label: 'GitHub Enterprise Self-Hosted',
  value: 'github_enterprise_self_hosted'
}, {
  label: 'GitLab',
  value: 'gitlab'
}, {
  label: "GitLab Enterprise",
  value: "gitlab_enterprise"
}];

const isDirty = computed(() => {
    return gitConfigId.value !== '' ||
        gitConfigUrl.value !== '' ||
        gitConfigProvider.value !== '' ||
        gitConfigUsername.value !== '' ||
        gitConfigPassword.value !== '' ||
        gitConfigCerts.value !== '' ||
        gitConfigSkipSSL.value !== false ||
        gitConfigGlobal.value !== false;
    }
);

const showDiscardConfirm = ref(false);

const validationPassed = computed(() => {
  if (!gitConfigId.value) return false;
  if (!gitConfigProvider.value) return false;
  if (!gitConfigUrl.value && (gitConfigProvider.value === 'github_enterprise_cloud' || gitConfigProvider.value === 'github_enterprise_self_hosted' || gitConfigProvider.value === 'gitlab_enterprise' || gitConfigProvider.value === 'git')) return false;

  const nameErrors = validateKubernetesName(gitConfigId.value, '', store.getters, undefined, []);
  return nameErrors.length === 0;
});

const canSave = computed(() => {
  const dirty = isDirty.value;
  const valid = validationPassed.value;
  return dirty && valid && !saving.value;
});

function openCreate() {
  errors.value = [];
  modalMode.value = 'create';
  gitConfigId.value = '';
  gitConfigUrl.value = '';
  gitConfigProvider.value = '';
  gitConfigUsername.value = '';
  gitConfigCerts.value = '';
  gitConfigPassword.value = '';
  gitConfigSkipSSL.value = false;
  gitConfigGlobal.value = false;
  showModal.value = true;
}

function handleModalClose() {
  if (isDirty.value) {
    showDiscardConfirm.value = true;
  } else {
    closeModal();
  }
}

function closeModal() {
  // Clear form state before setting showModal = false so that when Lit fires
  // modal-close (which triggers handleModalClose), isDirty is already false
  gitConfigId.value = '';
  gitConfigUrl.value = '';
  gitConfigProvider.value = '';
  gitConfigUsername.value = '';
  gitConfigPassword.value = '';
  gitConfigCerts.value = '';
  gitConfigSkipSSL.value = false;
  gitConfigGlobal.value = false;
  errors.value = [];
  showDiscardConfirm.value = false;
  showModal.value = false;
  hasAssociatedApps.value = false;
}

// canonicalInstanceUrl normalizes an enterprise instance URL to `https://<host>`
// (scheme + host, no path or trailing slash). The server matches a git config to
// a repository by reducing the repo URL to scheme://host, so the stored config
// URL must be in that same shape. Empty input (SaaS providers) is left empty.
function canonicalInstanceUrl(raw: string): string {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withScheme);
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return trimmed;
  }
}

async function onSubmit() {
  if (!validationPassed.value || !isDirty.value || saving.value) return;

  saving.value = true;
  errors.value = [];

  try {
    const gitConfig = await store.dispatch('epinio/create', { type: EPINIO_TYPES.GIT_CONFIG });

    gitConfig.id                = gitConfigId.value;
    gitConfig.url               = canonicalInstanceUrl(gitConfigUrl.value);
    gitConfig.provider          = gitConfigProvider.value;
    gitConfig.username          = gitConfigUsername.value;
    gitConfig.password          = gitConfigPassword.value;
    gitConfig.certs             = gitConfigCerts.value;
    gitConfig.skipssl           = gitConfigSkipSSL.value;
    gitConfig.global            = gitConfigGlobal.value;

    await gitConfig.create();
    store.dispatch('growl/success', {
        title:   t('epinio.growl.gitConfigs.create.success.title'),
        message: t('epinio.growl.gitConfigs.create.success.message', { name: gitConfigId.value }),
      });
    closeModal();
    store.dispatch('epinio/findAll', { type: EPINIO_TYPES.GIT_CONFIG, opt: { force: true } }).catch(() => {});
  } catch (err: any) {
    errors.value = epinioExceptionToErrorsArray(err);
    store.dispatch('growl/error', {
      title: t('epinio.growl.gitConfigs.save.error.createTitle'),
      message: t('epinio.growl.gitConfigs.save.error.message'),
    });
    console.error('Error saving git configuration:', err);
  } finally {
    saving.value = false;
  }
}

function handleKeepEditing() {
  showDiscardConfirm.value = false;
}

function handleDiscard() {
  showDiscardConfirm.value = false;
  closeModal();
}

__expose({ openCreate });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("trailhand-modal", {
    ".open": showModal.value,
    ".dismissible": false,
    title: "Git Configuration",
    subtitle: "Create New",
    position: "top",
    onModalClose: handleModalClose
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("trailhand-form-card", null, [
        _createElementVNode("trailhand-form-row", _hoisted_3, [
          _createElementVNode("trailhand-text-input", {
            value: gitConfigId.value,
            label: "Id",
            placeholder: "A Unique Identifier",
            required: true,
            onTextInputChange: _cache[0] || (_cache[0] = (e) => { gitConfigId.value = e.detail.value; })
          }, null, 40, _hoisted_4),
          _cache[8] || (_cache[8] = _createTextVNode()),
          _createElementVNode("trailhand-dropdown", {
            value: gitConfigProvider.value,
            label: "Provider",
            placeholder: "Git Provider (e.g. github, gitlab, etc.)",
            options: providerOptions,
            required: true,
            onDropdownChange: _cache[1] || (_cache[1] = (e) => { gitConfigProvider.value = e.detail.value; })
          }, null, 40, _hoisted_5)
        ]),
        _cache[10] || (_cache[10] = _createTextVNode()),
        (gitConfigProvider.value === 'github_enterprise_cloud' || gitConfigProvider.value === 'github_enterprise_self_hosted' || gitConfigProvider.value === 'gitlab_enterprise' || gitConfigProvider.value === 'git')
          ? (_openBlock(), _createElementBlock("trailhand-form-row", _hoisted_6, [
              _createElementVNode("trailhand-text-input", {
                value: gitConfigUrl.value,
                label: "URL",
                placeholder: "Git Host URL",
                required: true,
                onTextInputChange: _cache[2] || (_cache[2] = (e) => { gitConfigUrl.value = e.detail.value; })
              }, null, 40, _hoisted_7)
            ]))
          : _createCommentVNode("", true),
        _cache[11] || (_cache[11] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_8, [
          _createElementVNode("trailhand-text-input", {
            value: gitConfigUsername.value,
            label: "Username",
            placeholder: "Git Username",
            onTextInputChange: _cache[3] || (_cache[3] = (e) => { gitConfigUsername.value = e.detail.value; })
          }, null, 40, _hoisted_9),
          _cache[9] || (_cache[9] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: gitConfigPassword.value,
            label: "Password/Token",
            placeholder: "Git Password or Personal Access Token",
            type: "password",
            onTextInputChange: _cache[4] || (_cache[4] = (e) => { gitConfigPassword.value = e.detail.value; })
          }, null, 40, _hoisted_10)
        ]),
        _cache[12] || (_cache[12] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", null, [
          _createElementVNode("trailhand-text-area", {
            value: gitConfigCerts.value,
            label: "Certificate",
            placeholder: "Git Certificate",
            onTextAreaChange: _cache[5] || (_cache[5] = (e) => { gitConfigCerts.value = e.detail.value; })
          }, null, 40, _hoisted_11)
        ]),
        _cache[13] || (_cache[13] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_12, [
          _createElementVNode("trailhand-checkbox", {
            checked: gitConfigSkipSSL.value,
            onCheckboxChange: _cache[6] || (_cache[6] = (e) => { gitConfigSkipSSL.value = e.detail.checked; })
          }, "Skip SSL Verification", 40, _hoisted_13)
        ]),
        _cache[14] || (_cache[14] = _createTextVNode()),
        (isAdmin.value)
          ? (_openBlock(), _createElementBlock("trailhand-form-row", _hoisted_14, [
              _createElementVNode("trailhand-checkbox", {
                checked: gitConfigGlobal.value,
                onCheckboxChange: _cache[7] || (_cache[7] = (e) => { gitConfigGlobal.value = e.detail.checked; })
              }, "Global Configuration", 40, _hoisted_15)
            ]))
          : _createCommentVNode("", true)
      ]),
      _cache[15] || (_cache[15] = _createTextVNode()),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
        return (_openBlock(), _createBlock(Banner, {
          key: i,
          color: "error",
          label: err
        }, null, 8, ["label"]))
      }), 128))
    ]),
    _cache[20] || (_cache[20] = _createTextVNode()),
    _createElementVNode("div", _hoisted_16, [
      (showDiscardConfirm.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _cache[16] || (_cache[16] = _createElementVNode("span", { class: "discard-message" }, "You have unsaved changes.", -1)),
            _cache[17] || (_cache[17] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "secondary",
              class: "mr-10",
              onButtonClick: handleKeepEditing
            }, "\n          Keep Editing\n        ", 32),
            _cache[18] || (_cache[18] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "destructive",
              onButtonClick: handleDiscard
            }, "\n          Discard\n        ", 32)
          ], 64))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("trailhand-button", {
              variant: "secondary",
              class: "mr-10",
              onButtonClick: handleModalClose
            }, "\n          Cancel\n        ", 32),
            _cache[19] || (_cache[19] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "primary",
              disabled: !canSave.value,
              onButtonClick: onSubmit
            }, "\n          Create\n        ", 40, _hoisted_17)
          ], 64))
    ])
  ], 40, _hoisted_1))
}
}

})